import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import { Badge } from "@admin-ui/components/primitives/base/badges/badges";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Dropdown } from "@admin-ui/components/primitives/base/dropdown/dropdown";
import { formatCount } from "@admin-ui/utils/format-count";
import { Head, router } from "@inertiajs/react";
import {
  adminDestinationPath,
  adminDestinationsCapitalCityBackfillsPath,
  adminDestinationsClimateBackfillsPath,
  adminDestinationsFactorsPath,
  adminDestinationsLivingCostBackfillsPath,
  adminDestinationsMarketingSentenceBackfillsPath,
  adminDestinationsPath,
  adminDestinationsReindexPath,
  adminDestinationsTimezoneBackfillsPath,
  editAdminDestinationPath,
  newAdminDestinationPath,
} from "@travily/routes";
import type AdminDestinationsIndex from "@travily/types/pages/AdminDestinationsIndex";
import { Activity, Announcement01, Clock, Cloud01, CoinsHand, Globe01, Plus, RefreshCcw01 } from "@untitledui/icons";
import { type ReactNode, useCallback } from "react";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import { useAdminListNavigation } from "@/hooks/admin/use-admin-list-navigation";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import { type DestinationFilters, IndexView } from "./index.view";

type AdminDestinationsIndexPageProps = AdminDestinationsIndex;

const FILTER_TO_RANSACK: Record<keyof DestinationFilters, string> = {
  search: "name_or_country_name_cont",
};

function AdminDestinationsIndexPage({ destinations, pagy, sort, sort_dir, filters }: AdminDestinationsIndexPageProps) {
  const account = useAdminLayoutAccount();

  const { handleSortChange, handlePageChange, handleSearchChange } = useAdminListNavigation<DestinationFilters>({
    basePath: adminDestinationsPath(),
    filters,
    sort,
    sortDir: sort_dir,
    filterToRansack: FILTER_TO_RANSACK,
  });

  const handleNewDestination = useCallback(() => router.get(newAdminDestinationPath()), []);

  const handleFactorHealth = useCallback(() => router.get(adminDestinationsFactorsPath()), []);

  const handleEditDestination = useCallback(
    (destinationId: number) => router.get(editAdminDestinationPath(destinationId)),
    [],
  );

  const handleDeleteDestination = useCallback(
    (destinationId: number) => router.delete(adminDestinationPath(destinationId)),
    [],
  );

  const handleReindex = useCallback(
    () => router.post(adminDestinationsReindexPath(), {}, { preserveScroll: true }),
    [],
  );

  const handleBackfillTimezones = useCallback(
    () => router.post(adminDestinationsTimezoneBackfillsPath(), {}, { preserveScroll: true }),
    [],
  );

  const handleBackfillClimates = useCallback(
    () => router.post(adminDestinationsClimateBackfillsPath(), {}, { preserveScroll: true }),
    [],
  );

  const handleBackfillLivingCosts = useCallback(
    () => router.post(adminDestinationsLivingCostBackfillsPath(), {}, { preserveScroll: true }),
    [],
  );

  const handleSeedCapitalCities = useCallback(
    () => router.post(adminDestinationsCapitalCityBackfillsPath(), {}, { preserveScroll: true }),
    [],
  );

  const handleBackfillMarketingSentences = useCallback(
    () => router.post(adminDestinationsMarketingSentenceBackfillsPath(), {}, { preserveScroll: true }),
    [],
  );

  const destinationCount = formatCount(pagy.count, "destination");

  return (
    <AdminLayout activeUrl={adminDestinationsPath()} account={account}>
      <Head title="Destinations - Admin" />

      <AdminPageHeader
        breadcrumbs={[]}
        title="Destinations"
        titleBadge={
          <Badge color="gray" size="sm">
            {destinationCount}
          </Badge>
        }
        subtitle="Curated travel destinations with location matching"
        actions={
          <>
            <Button size="sm" color="secondary" iconLeading={Activity} onClick={handleFactorHealth}>
              Factor health
            </Button>
            <Button size="sm" color="primary" iconLeading={Plus} onClick={handleNewDestination}>
              New destination
            </Button>
            <Dropdown.Root>
              <Dropdown.DotsButton />
              <Dropdown.Popover className="w-min">
                <Dropdown.Menu
                  onAction={(key) => {
                    if (key === "reindex") handleReindex();
                    if (key === "backfill_timezones") handleBackfillTimezones();
                    if (key === "backfill_climates") handleBackfillClimates();
                    if (key === "backfill_living_costs") handleBackfillLivingCosts();
                    if (key === "seed_capital_cities") handleSeedCapitalCities();
                    if (key === "backfill_marketing_sentences") handleBackfillMarketingSentences();
                  }}
                >
                  <Dropdown.Item id="reindex" icon={RefreshCcw01} label="Reindex destinations" />
                  <Dropdown.Item id="backfill_timezones" icon={Clock} label="Backfill timezones" />
                  <Dropdown.Item id="backfill_climates" icon={Cloud01} label="Backfill climates" />
                  <Dropdown.Item id="backfill_living_costs" icon={CoinsHand} label="Backfill cost of living" />
                  <Dropdown.Item id="seed_capital_cities" icon={Globe01} label="Seed capital cities" />
                  <Dropdown.Item
                    id="backfill_marketing_sentences"
                    icon={Announcement01}
                    label="Backfill marketing sentences"
                  />
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          </>
        }
      />

      <div className="mx-auto flex w-full max-w-container flex-col gap-6 px-4 lg:gap-8 lg:px-8">
        <IndexView
          destinations={destinations}
          pagy={pagy}
          sort={sort}
          sortDir={sort_dir}
          filters={filters}
          onSortChange={handleSortChange}
          onPageChange={handlePageChange}
          onSearchChange={handleSearchChange}
          onEditDestination={handleEditDestination}
          onDeleteDestination={handleDeleteDestination}
        />
      </div>
    </AdminLayout>
  );
}

AdminDestinationsIndexPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminDestinationsIndexPage;
